/**
 * t278u: chat bubbles. Consecutive messages from the same person within five minutes (same London day) form a run:
 * the name shows once above the first bubble (group chats and channels only), the avatar beside the last, and the
 * corners on the sender's side tighten between bubbles of the same run.
 */
import type { Message } from '@tradeoutlet/shared';
import { dayDivider } from '@/lib/format';

export const RUN_GAP_MS = 5 * 60_000;

/**
 * t287u: the desktop chat column for messages, separators and the composer (the header stays full width). Wide enough
 * for forms and polls, narrow enough that your bubbles and theirs stay close together on 1440px and wider screens.
 */
export const CHAT_COLUMN = 'mx-auto w-full max-w-[880px]';

export interface BubbleRun {
  /** First bubble of a run: carries the sender name in group chats. */
  first: boolean;
  /** Last bubble of a run: carries the avatar and the time. */
  last: boolean;
  /** Show names and avatars (group DMs, channels, rooms). Undefined: decided from the conversation kind. */
  names?: boolean;
}

type RunMsg = Pick<Message, 'id' | 'sender_id' | 'kind' | 'created_at' | 'deleted_at'>;

/** `m` continues `prev`'s run: same sender, both ordinary messages, within five minutes on the same day. */
export function continuesRun(prev: RunMsg | null | undefined, m: RunMsg): boolean {
  if (!prev) return false;
  if (prev.kind !== 'user' || m.kind !== 'user' || prev.sender_id !== m.sender_id) return false;
  const gap = Date.parse(m.created_at) - Date.parse(prev.created_at);
  return gap >= 0 && gap < RUN_GAP_MS && dayDivider(prev.created_at) === dayDivider(m.created_at);
}

/** Run position of every message in display order. `breaks`: ids that start a new run anyway (e.g. after the New marker). */
export function runPositions(list: readonly RunMsg[], breaks?: ReadonlySet<string>): Map<string, BubbleRun> {
  const out = new Map<string, BubbleRun>();
  list.forEach((m, i) => {
    const prev = list[i - 1], next = list[i + 1];
    const first = !continuesRun(prev, m) || Boolean(breaks?.has(m.id));
    const last = !next || !continuesRun(m, next) || Boolean(breaks?.has(next.id));
    out.set(m.id, { first, last });
  });
  return out;
}

/** 1:1 DMs show no names or avatars; group DMs, channels and rooms do. */
export const bubbleNames = (kind: string | undefined) => kind !== 'dm';

/**
 * Corner classes: fully rounded, with a tighter corner on the sender's side between bubbles of one run
 * (yours on the right, everyone else's on the left).
 */
export function bubbleCorners(mine: boolean, run: Pick<BubbleRun, 'first' | 'last'>): string {
  const c = ['rounded-2xl'];
  if (mine) { if (!run.first) c.push('rounded-tr-md'); if (!run.last) c.push('rounded-br-md'); }
  else { if (!run.first) c.push('rounded-tl-md'); if (!run.last) c.push('rounded-bl-md'); }
  return c.join(' ');
}
